import { PALETTE } from '@overlayqa/shared';
/**
 * Custom branding settings (spec 2026-09-01; UX pass 2026-09-03 on Emily's
 * "proceed with all recos" for the audit of this section).
 *
 * Sets the logo, accent colour, header background and attribution toggle that
 * appear on the three pages a team's CLIENTS see: the guest client review, the
 * review portal, and the shared issues page.
 *
 * IT RENDERS FOR A NON-ENTITLED TEAM TOO, with the controls disabled and the
 * upgrade path shown. Hiding the section would hide the feature from exactly
 * the people being asked to buy it, which is why the GET route is deliberately
 * not plan-gated while every write is.
 *
 * CONTRAST IS THE SERVER'S ANSWER, NOT A SECOND OPINION. The accent must clear
 * 4.5:1 against the page background, and that judgement lives in
 * services/brandingContrast.ts. The live preview and its readout are the
 * server's document and verdict (BrandingPreview.tsx); the only colour maths in
 * this package is `normalizeHexInput`, which mirrors the server's ACCEPTED
 * SPELLINGS so the swatch and the dirty check read `#fff` the way the server
 * will, and never whether a colour is readable.
 *
 * THREE COMMIT MODELS BECAME TWO. The two colours share one Save (Emily's
 * decision 13(a)): disabled until something changed, Enter submits, "Saved."
 * sits beside the button and clears on the next edit. The checkbox and the
 * logo stay immediate, because nothing about their value can be refused.
 * Every message renders under the control that caused it.
 */
import { useEffect, useRef, useState, type ChangeEvent, type FormEvent } from 'react';
import { useAppSession } from '../../hooks/useAppSession';
import {
  useTeamBranding,
  useUpdateTeamBranding,
  useUploadTeamLogo,
  useDeleteTeamLogo,
} from '../../hooks/useTeams';
import { normalizeHexInput } from '../../lib/hexColour';
import { log } from '@/lib/logger';
import { capture } from '@/lib/analytics';
import { ConfirmDialog } from '../shared/ConfirmDialog';
import { Tooltip } from '../shared/Tooltip';
import { LockIcon } from '@overlayqa/shared/ui';
import { BrandingPreview } from './BrandingPreview';
import { BrandingLogoWarning } from './BrandingLogoWarning';
import { BrandingLogoDropzone, DEFAULT_HEADER_BACKGROUND } from './BrandingLogoDropzone';

log.info('[BrandingSection] Module loaded');

const MAX_LOGO_BYTES = 10 * 1024 * 1024;
/** Mirrors ALLOWED_LOGO_TYPES in routes/teamBranding.ts; SVG since 2026-09-03. */
const ACCEPTED = 'image/png,image/svg+xml,image/jpeg,image/webp,image/gif';
const DEFAULT_ACCENT = PALETTE.brand.primary;
/** How long "Saved." stays after a save when the user does not edit again. */
const SAVED_VISIBLE_MS = 4000;
/** The lock beside the hide checkbox for a team not on a paid plan (Emily, 2026-11-01). */
const HIDE_POWERED_BY_LOCKED = 'Upgrade to hide "Powered by OverlayQA"';

interface ServerRefusal {
  message: string;
  suggestion?: string | null;
}

/**
 * Pull the message and any suggested colour out of a thrown API error.
 *
 * `ApiError` carries the parsed response envelope on `details`, NOT on `body`
 * (apiError.ts: `constructor(status, message, code, details)`). Reading the
 * wrong field is quiet rather than loud: `message` still resolves off the Error
 * itself, so the refusal renders correctly and only the SUGGESTION silently
 * disappears -- which is exactly what the first live run caught.
 */
function readRefusal(err: unknown): ServerRefusal {
  const anyErr = err as {
    message?: string;
    details?: { error?: { message?: string; suggestion?: string | null } };
  };
  const envelope = anyErr?.details?.error;
  return {
    message: envelope?.message ?? anyErr?.message ?? 'Could not save. Try again.',
    suggestion: envelope?.suggestion ?? null,
  };
}

/**
 * Log a save failure at the RIGHT level.
 *
 * A refused colour or an oversized file is the user being told something, not
 * the application breaking. Logging those at `error` mints fake exceptions in
 * the error trackers. A 4xx we recognise is `warn`; anything else stays
 * `error`, because a 500 or a dropped connection genuinely is one.
 */
function logRefusal(where: string, err: unknown): void {
  const status = (err as { status?: number })?.status;
  const handled = typeof status === 'number' && status >= 400 && status < 500;
  const payload = { error: err instanceof Error ? err.message : String(err) };
  if (handled) log.warn(`[BrandingSection] ${where} refused`, payload);
  else log.error(`[BrandingSection] ${where} failed`, payload);
}

/**
 * The comparison key for a colour field: the canonical hex when the text is a
 * colour in any accepted spelling, else the raw text. `#fff` and `#FFFFFF` are
 * the same colour and must not read as an unsaved change; `zzz` against an
 * empty baseline must.
 */
function colourKey(value: string): string {
  return normalizeHexInput(value) ?? value.trim().toUpperCase();
}

function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

function BrandingSkeleton() {
  return (
    <div data-testid="branding-skeleton" aria-busy="true" className="space-y-5">
      <div className="h-4 w-3/4 rounded skeleton-shimmer" />
      <div className="h-24 rounded-md skeleton-shimmer" />
      <div className="flex items-center gap-4">
        <div className="h-16 w-40 rounded-md skeleton-shimmer" />
        <div className="h-9 w-24 rounded-md skeleton-shimmer" />
      </div>
      <div className="h-10 w-2/3 rounded-md skeleton-shimmer" />
      <div className="h-10 w-2/3 rounded-md skeleton-shimmer" />
    </div>
  );
}

export function BrandingSection() {
  const { data: session } = useAppSession();
  const teamId = session?.team?.id;

  const brandingQuery = useTeamBranding(teamId);
  const updateMutation = useUpdateTeamBranding(teamId);
  const uploadMutation = useUploadTeamLogo(teamId);
  const deleteLogoMutation = useDeleteTeamLogo(teamId);

  const branding = brandingQuery.data?.branding;
  const entitled = brandingQuery.data?.entitled ?? false;
  const sampleReviewUrl = brandingQuery.data?.sampleReviewUrl ?? null;
  // Only a paid plan may hide the footer; a trial team's client pages always show it.
  // Absent only from an API that predates the field (Vercel lands before Railway
  // on one push), which still honours every stored hide: keep its old rule.
  const canHidePoweredBy = brandingQuery.data?.canHidePoweredBy ?? entitled;

  const fileRef = useRef<HTMLInputElement>(null);

  // ── Colours: fields, their saved baseline, and the one Save ──────────────
  const [accent, setAccent] = useState('');
  const [background, setBackground] = useState('');
  const [baseline, setBaseline] = useState<{ accent: string; background: string } | null>(null);
  const [savingColours, setSavingColours] = useState(false);
  const [coloursRefusal, setColoursRefusal] = useState<ServerRefusal | null>(null);
  const [coloursSaved, setColoursSaved] = useState(false);

  // Seed the fields and the baseline ONCE when the server's values arrive. A
  // later refetch (after our own save) carries what we just wrote, and the
  // save path has already moved the baseline, so re-seeding would only clobber
  // whatever the user has typed since.
  useEffect(() => {
    if (baseline !== null || !branding) return;
    const a = branding.accentColor ?? '';
    const b = branding.backgroundColor ?? '';
    setAccent(a);
    setBackground(b);
    setBaseline({ accent: colourKey(a), background: colourKey(b) });
  }, [branding, baseline]);

  useEffect(() => {
    if (!coloursSaved) return;
    const timer = setTimeout(() => setColoursSaved(false), SAVED_VISIBLE_MS);
    return () => clearTimeout(timer);
  }, [coloursSaved]);

  const accentDirty = baseline !== null && colourKey(accent) !== baseline.accent;
  const backgroundDirty = baseline !== null && colourKey(background) !== baseline.background;
  const dirty = accentDirty || backgroundDirty;
  const canSave = entitled && !savingColours && dirty;

  const editAccent = (value: string) => { setAccent(value); setColoursSaved(false); };
  const editBackground = (value: string) => { setBackground(value); setColoursSaved(false); };

  /**
   * Write a colour patch. Called by the form (with whatever changed) and by
   * the two "Use #xxxxxx" buttons (with the suggestion), so the suggestion is
   * one click: apply AND save.
   */
  const commitColours = async (patch: { accentColor?: string | null; backgroundColor?: string | null }) => {
    if (Object.keys(patch).length === 0) return;
    setColoursRefusal(null);
    setColoursSaved(false);
    setSavingColours(true);
    try {
      await updateMutation.mutateAsync(patch);
      if (patch.accentColor !== undefined) {
        capture('team_branding_updated', { field: 'accent', has_accent: patch.accentColor !== null });
      }
      if (patch.backgroundColor !== undefined) {
        capture('team_branding_updated', { field: 'background', has_background: patch.backgroundColor !== null });
      }
      // The server stores the canonical spelling; show it, and move the
      // baseline so the button reads "nothing left to save".
      const nextAccent = patch.accentColor !== undefined ? (patch.accentColor === null ? '' : normalizeHexInput(patch.accentColor) ?? patch.accentColor) : accent;
      const nextBackground = patch.backgroundColor !== undefined ? (patch.backgroundColor === null ? '' : normalizeHexInput(patch.backgroundColor) ?? patch.backgroundColor) : background;
      setAccent(nextAccent);
      setBackground(nextBackground);
      setBaseline({ accent: colourKey(nextAccent), background: colourKey(nextBackground) });
      setColoursSaved(true);
    } catch (err) {
      logRefusal('colour save', err);
      setColoursRefusal(readRefusal(err));
    } finally {
      setSavingColours(false);
    }
  };

  const onSubmitColours = (ev: FormEvent<HTMLFormElement>) => {
    ev.preventDefault();
    if (!canSave) return;
    const patch: { accentColor?: string | null; backgroundColor?: string | null } = {};
    if (accentDirty) patch.accentColor = accent.trim() === '' ? null : accent.trim();
    if (backgroundDirty) patch.backgroundColor = background.trim() === '' ? null : background.trim();
    void commitColours(patch);
  };

  const applySuggestedAccent = (hex: string) => {
    setAccent(hex);
    void commitColours({ accentColor: hex });
  };

  // ── Attribution toggle: immediate, reverts on refusal ───────────────────
  /**
   * Local mirror of the toggle. A checkbox bound straight to query data does
   * not move when clicked: React re-renders from the unchanged cache before
   * the PATCH and its invalidation land, so the control reads as dead for the
   * whole round trip. Seeded from the server, moved immediately on click, and
   * reverted if the save fails.
   */
  const [hideAttribution, setHideAttribution] = useState<boolean | null>(null);
  const [togglingAttribution, setTogglingAttribution] = useState(false);
  const [attributionRefusal, setAttributionRefusal] = useState<ServerRefusal | null>(null);
  useEffect(() => {
    if (hideAttribution === null && branding?.hidePoweredBy !== undefined) {
      setHideAttribution(branding.hidePoweredBy);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branding?.hidePoweredBy]);

  // What the client pages actually do: a stored hide shows the footer anyway
  // until the team pays, so the checkbox and the preview say the same.
  const effectiveHide = canHidePoweredBy && (hideAttribution ?? branding?.hidePoweredBy ?? false);

  const onToggleAttribution = async (next: boolean) => {
    setAttributionRefusal(null);
    setHideAttribution(next);
    setTogglingAttribution(true);
    try {
      await updateMutation.mutateAsync({ hidePoweredBy: next });
      capture('team_branding_updated', { field: 'hide_powered_by', hide_powered_by: next });
    } catch (err) {
      logRefusal('attribution toggle', err);
      setHideAttribution(!next); // the save failed; do not leave a lie on screen
      setAttributionRefusal(readRefusal(err));
    } finally {
      setTogglingAttribution(false);
    }
  };

  // ── Logo: drop or choose, uploads at once; remove asks first ────────────
  const [logoRefusal, setLogoRefusal] = useState<ServerRefusal | null>(null);
  const [confirmRemove, setConfirmRemove] = useState(false);
  // In-flight state is tracked here, per control, so a colour save never greys
  // out the logo and vice versa (the old single `busy` disabled everything).
  const [uploading, setUploading] = useState(false);
  const [removing, setRemoving] = useState(false);
  const logoDisabled = !entitled || uploading || removing;

  const uploadLogo = async (file: File) => {
    setLogoRefusal(null);
    // Checked here so an oversized file is refused before it is read into
    // memory and base64-inflated by a third. The server checks again.
    if (file.size > MAX_LOGO_BYTES) {
      setLogoRefusal({ message: 'That image is over 10MB. Try a smaller one.' });
      return;
    }
    const dataUrl = await readFileAsDataUrl(file).catch((err) => {
      log.error('[BrandingSection] could not read file', { error: String(err) });
      return '';
    });
    if (!dataUrl) {
      setLogoRefusal({ message: 'Could not read that file. Try another image.' });
      return;
    }
    setUploading(true);
    try {
      await uploadMutation.mutateAsync({ dataUrl, filename: file.name });
      capture('team_branding_logo_uploaded', { bytes: file.size, mime: file.type });
    } catch (err) {
      logRefusal('logo upload', err);
      setLogoRefusal(readRefusal(err));
    } finally {
      setUploading(false);
    }
  };

  const onPickFile = async (ev: ChangeEvent<HTMLInputElement>) => {
    const file = ev.target.files?.[0];
    // Reset first so choosing the same file twice fires change again.
    if (fileRef.current) fileRef.current.value = '';
    if (!file) return;
    await uploadLogo(file);
  };

  const onDropFiles = (files: FileList | File[]) => {
    const file = Array.from(files)[0];
    if (file) void uploadLogo(file);
  };

  const onConfirmRemove = async () => {
    setLogoRefusal(null);
    setRemoving(true);
    try {
      await deleteLogoMutation.mutateAsync();
    } catch (err) {
      logRefusal('logo remove', err);
      setLogoRefusal(readRefusal(err));
    } finally {
      setRemoving(false);
      setConfirmRemove(false);
    }
  };

  if (brandingQuery.isLoading || !branding) {
    return <BrandingSkeleton />;
  }

  const savedBackground = normalizeHexInput(background) && !backgroundDirty
    ? normalizeHexInput(background)
    : branding.backgroundColor;

  return (
    <div className="space-y-5">
      <p className="text-sm text-gray-600 prose-measure">
        Your logo and colour on the pages your clients see: client links, the review portal, and
        shared issue links. The header background applies to client links.
      </p>

      {/* `entitled` is false only for a team with no active plan (trial ended, or
          paid then cancelled): branding is on every plan since 2026-09-16, so the
          panel names the lapse, never a tier. Same construction as the trial
          chip's "Pick a plan to keep capturing." */}
      {!entitled && (
        <div className="rounded-md border border-gray-200 bg-gray-50 p-3 text-sm text-gray-700">
          Pick a plan to change your branding.{' '}
          <a className="text-primary-600 font-medium hover:underline" href="/billing">
            See plans
          </a>
        </div>
      )}

      <BrandingPreview
        teamId={teamId}
        accent={accent}
        background={background}
        hidePoweredBy={effectiveHide}
        logoUrl={branding.logoUrl}
        sampleReviewUrl={sampleReviewUrl}
        onUseSuggestion={applySuggestedAccent}
        disabled={!entitled || savingColours}
      />

      {/* ── Logo ─────────────────────────────────────────────── */}
      <div data-testid="branding-logo-field">
        <h3 className="section-title">Logo</h3>
        <div className="mt-2 flex items-center gap-4">
          <BrandingLogoDropzone
            logoUrl={branding.logoUrl}
            backgroundColor={savedBackground ?? DEFAULT_HEADER_BACKGROUND}
            disabled={logoDisabled}
            uploading={uploading}
            onFiles={onDropFiles}
            onChoose={() => fileRef.current?.click()}
          />
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="btn btn-secondary"
              disabled={logoDisabled}
              onClick={() => fileRef.current?.click()}
            >
              <span className="text-cap-trim">{branding.logoUrl ? 'Replace' : 'Upload'}</span>
            </button>
            {branding.logoUrl && (
              <button
                type="button"
                className="btn btn-ghost"
                disabled={logoDisabled}
                onClick={() => setConfirmRemove(true)}
              >
                <span className="text-cap-trim">Remove</span>
              </button>
            )}
          </div>
          <input
            ref={fileRef}
            type="file"
            accept={ACCEPTED}
            className="hidden"
            onChange={onPickFile}
            data-testid="branding-logo-input"
          />
        </div>
        <p className="mt-2 text-xs text-gray-500 prose-measure">
          PNG, SVG, JPEG, WebP, or GIF up to 10MB. It shows at up to 160 x 28 px. Choose artwork
          that reads clearly on your header colour and on light shared pages.
        </p>
        {logoRefusal && (
          <div role="alert" className="mt-2 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">
            {logoRefusal.message}
          </div>
        )}
        <BrandingLogoWarning
          teamId={teamId}
          logoUrl={branding.logoUrl}
          accent={accent.trim()}
          background={background.trim()}
          hidePoweredBy={effectiveHide}
        />
      </div>

      {/* ── Colours: one form, one Save ───────────────────────── */}
      <form aria-label="Colours" onSubmit={onSubmitColours} className="space-y-4">
        <div>
          <h3 className="section-title">Accent colour</h3>
          <div className="mt-2 flex items-center gap-2">
            <input
              type="color"
              aria-label="Accent colour swatch"
              className="h-10 w-12 cursor-pointer rounded border border-gray-200"
              value={normalizeHexInput(accent) ?? DEFAULT_ACCENT}
              disabled={!entitled || savingColours}
              onChange={(e) => editAccent(e.target.value.toUpperCase())}
            />
            <input
              type="text"
              className="input input-lg w-36"
              aria-label="Accent colour hex"
              placeholder={DEFAULT_ACCENT}
              value={accent}
              disabled={!entitled || savingColours}
              onChange={(e) => editAccent(e.target.value)}
            />
            {accent.trim() !== '' && (
              <button
                type="button"
                className="text-xs font-medium text-gray-500 hover:text-gray-700 hover:underline"
                aria-label="Reset accent colour to default"
                disabled={!entitled || savingColours}
                onClick={() => editAccent('')}
              >
                <span className="text-cap-trim block">Reset to default</span>
              </button>
            )}
          </div>
          <p className="mt-2 text-xs text-gray-500">
            The colour of links and highlights on your client pages. Needs enough contrast to stay
            readable on a light page.
          </p>
        </div>

        <div>
          <h3 className="section-title">Header background</h3>
          <div className="mt-2 flex items-center gap-2">
            <input
              type="color"
              aria-label="Header background swatch"
              className="h-10 w-12 cursor-pointer rounded border border-gray-200"
              value={normalizeHexInput(background) ?? DEFAULT_HEADER_BACKGROUND}
              disabled={!entitled || savingColours}
              onChange={(e) => editBackground(e.target.value.toUpperCase())}
            />
            <input
              type="text"
              className="input input-lg w-36"
              aria-label="Header background hex"
              placeholder={DEFAULT_HEADER_BACKGROUND}
              value={background}
              disabled={!entitled || savingColours}
              onChange={(e) => editBackground(e.target.value)}
            />
            {background.trim() !== '' && (
              <button
                type="button"
                className="text-xs font-medium text-gray-500 hover:text-gray-700 hover:underline"
                aria-label="Reset header background to default"
                disabled={!entitled || savingColours}
                onClick={() => editBackground('')}
              >
                <span className="text-cap-trim block">Reset to default</span>
              </button>
            )}
          </div>
          <p className="mt-2 text-xs text-gray-500 prose-measure">
            The bar behind your logo on client links. Text and icons adjust to stay readable.
            Your uploaded logo keeps its original colours.
          </p>
        </div>

        <div className="flex items-center gap-3">
          <button type="submit" className="btn btn-primary" disabled={!canSave}>
            <span className="text-cap-trim">{savingColours ? 'Saving...' : 'Save'}</span>
          </button>
          {coloursSaved && !dirty && (
            <span role="status" className="text-sm text-green-700">Saved.</span>
          )}
          {dirty && !savingColours && (
            <span className="text-xs text-gray-500">Unsaved changes</span>
          )}
        </div>

        {coloursRefusal && (
          <div role="alert" className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">
            {coloursRefusal.message}
            {coloursRefusal.suggestion && (
              <button
                type="button"
                className="ml-2 font-medium underline"
                disabled={savingColours}
                onClick={() => applySuggestedAccent(coloursRefusal.suggestion as string)}
              >
                <span className="text-cap-trim block">Use {coloursRefusal.suggestion}</span>
              </button>
            )}
          </div>
        )}
      </form>

      {/* ── Attribution ──────────────────────────────────────── */}
      <div>
        <div className="flex items-start gap-3">
          <input
            id="branding-hide-powered-by"
            type="checkbox"
            className="mt-1"
            aria-describedby="branding-hide-powered-by-help"
            disabled={!entitled || !canHidePoweredBy || togglingAttribution}
            checked={effectiveHide}
            onChange={(e) => onToggleAttribution(e.target.checked)}
          />
          <div className="text-sm text-gray-700">
            <div className="flex items-center gap-1">
              <label htmlFor="branding-hide-powered-by" className="font-medium">
                Hide &quot;Powered by OverlayQA&quot;
              </label>
              {/* Outside the label so the reason is not read as part of the
                  checkbox's name; the message is the button's own name because
                  a tooltip is not announced on its own. */}
              {!canHidePoweredBy && (
                <Tooltip label={HIDE_POWERED_BY_LOCKED} placement="top" multiline>
                  <button
                    type="button"
                    aria-label={HIDE_POWERED_BY_LOCKED}
                    className="inline-flex rounded-full text-gray-500 hover:text-gray-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
                  >
                    <LockIcon />
                  </button>
                </Tooltip>
              )}
            </div>
            <span id="branding-hide-powered-by-help" className="mt-1 block text-xs text-gray-500">
              Applies to client links, the review portal, and shared issue pages.
            </span>
          </div>
        </div>
        {attributionRefusal && (
          <div role="alert" className="mt-2 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">
            {attributionRefusal.message}
          </div>
        )}
      </div>

      <ConfirmDialog
        open={confirmRemove}
        title="Remove logo?"
        message="Client pages go back to the OverlayQA logo. You can upload a new one any time."
        confirmLabel="Remove"
        destructive
        loading={removing}
        onConfirm={() => { void onConfirmRemove(); }}
        onCancel={() => setConfirmRemove(false)}
      />
    </div>
  );
}
